import { useCallback, useEffect, useRef, useState } from 'react';
import { Storage } from '../lib/storage';
import { playerSurfaceActive } from '../lib/api/customerContext';
import {
  appendMessage,
  broadcastFromClub,
  createGroup,
  emptyMessaging,
  ensureClubConversation,
  ensureDm,
  markRead,
  mergeMessaging,
  MESSAGING_KEY,
  sanitizeMessaging,
  type Conversation,
  type DynamicRule,
  type MessagingState,
} from '../engine/messaging';
import {
  conversationBlockedBy,
  ensureDynamicConversation,
  isMessagingDisabled,
  setLeftDynamic,
  setMessagingDisabled,
  setMuted,
} from '../engine/messagingGroups';

// ─────────────────────────────────────────────────────────────────────
// useMessaging — persistence for the Messenger-style conversations.
//
// One blob under `messages_v1` (the same client path every mobile feature uses
// today — see docs/architecture/SECURITY.md for what that store is). The engine is pure;
// this hook only reads, merges and writes:
//
//   • Storage-first: every mutation re-reads the stored blob, merges it with
//     the local state (mergeMessaging — union by id), applies the change,
//     `await Storage.set`, THEN flips state. Writes to the key are serialised
//     across every writer IN THIS TAB; across tabs and devices they are not,
//     and cannot be from the client (see commit(), and #240).
//   • Live: while `live` is true the blob is polled (POLL_MS) and on window
//     focus/online, like the banepad heartbeat reader. No sockets exist yet.
//
// When the member API lands, this hook is the only place that changes:
// `/member/messages` replaces read/write and the engine stays as it is.
// ─────────────────────────────────────────────────────────────────────

export const MESSAGING_POLL_MS = 8000;

const newId = () => `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Thrown when a write is attempted on a surface that has no messaging (#393).
 *
 * Its own class so a caller can tell «this surface cannot do that» apart from
 * «the write failed», which are different things to put in front of a person.
 */
export class MessagingUnavailable extends Error {
  constructor() {
    super('[messaging] Not available on this surface (#393). Member scope is #236.');
    this.name = 'MessagingUnavailable';
  }
}

export interface MessagingApi {
  state: MessagingState;
  loaded: boolean;
  /**
   * Messaging cannot be used on this surface (#393).
   *
   * True in MEMBER MODE. Meldinger lives in the club-scoped `messages_v1` KV
   * blob, and a member session is deliberately not a club session — so there
   * is no member-readable copy of it to show and nowhere to put a reply.
   *
   * The caller must TELL the person. Rendering an empty inbox would be a lie
   * that gets worse on send: the message would appear, go nowhere, and vanish
   * on the next merge. Server-side participant scope is #236.
   */
  unavailable: boolean;
  /** Re-read the stored blob now (merged into local state). */
  refresh: () => Promise<void>;
  send: (conversationId: string, senderId: unknown, text: string) => Promise<void>;
  openDm: (a: unknown, b: unknown) => Promise<Conversation>;
  openGroup: (creatorId: unknown, memberIds: unknown[], title: string) => Promise<Conversation>;
  openClubConversation: (memberId: unknown) => Promise<Conversation>;
  read: (conversationId: string, readerId: unknown) => Promise<void>;
  /** The club sends one text to every listed member. */
  broadcast: (memberIds: unknown[], text: string) => Promise<{ count: number }>;
  /** The stored record behind an automatic club group (created on first use). */
  openDynamic: (rule: DynamicRule) => Promise<Conversation>;
  /** Mute / unmute one conversation for one member. */
  mute: (memberId: unknown, conversationId: string, muted: boolean) => Promise<void>;
  /** Leave (or rejoin) ALL of a club's automatic groups. */
  leaveDynamic: (memberId: unknown, clubId: string, left: boolean) => Promise<void>;
  /** Switch the whole messaging module off / on for one member. */
  setDisabled: (memberId: unknown, disabled: boolean) => Promise<void>;
}

export interface UseMessagingOptions {
  /**
   * May this session use Meldinger at all? Default true.
   *
   * False for a club session whose role lacks `messaging.send_as_organization`
   * (#236, 2026-10-31): the server refuses `messages_v1` on `/kv` with 403, so
   * the hook reads nothing, polls nothing, reports `unavailable` and refuses
   * every write — rather than fetching a key it may not read and showing an
   * empty inbox as if the club had no messages.
   */
  enabled?: boolean;
  /** Poll the store while a messaging screen is visible. */
  live?: boolean;
  pollMs?: number;
  now?: () => number;
  nextId?: () => string;
}

// ─── write serialisation ───
//
// Keyed by STORAGE KEY, and deliberately module-level rather than a ref: the
// thing two writers contend over is the key, not the hook. More than one
// useMessaging can be mounted in one tab — AppShell owns one and MobileApp
// creates its own, both writing MESSAGING_KEY — so a per-hook queue would give
// each of them a private chain and they would race exactly as they did before
// any of this. Anything that writes the same key shares this chain.
//
// SCOPE: one JavaScript runtime. See commit() for what this does NOT solve.
const writeQueues = new Map<string, Promise<void>>();

/** Run `job` after every job already queued for `key`. */
function serialise<T>(key: string, job: () => Promise<T>): Promise<T> {
  const run = (writeQueues.get(key) ?? Promise.resolve()).then(job);
  // A rejected job (an empty message, a failed write) must not poison the chain
  // for everything queued behind it.
  writeQueues.set(
    key,
    run.then(
      () => undefined,
      () => undefined,
    ),
  );
  return run;
}

/** Tests only: drop the chains so one test cannot wait on another's writes. */
export function __resetMessagingWriteQueue(): void {
  writeQueues.clear();
}

async function readStored(): Promise<MessagingState> {
  try {
    // MEMBER MODE READS NOTHING (#393).
    //
    // This was the last hook touching club `/kv` without the guard that
    // `useInitialLoad`, `useClubInfo`, `useBookingActions` and
    // `useMemberBootstrap` all carry, and the cost was not a failed read: a
    // member token is `spm_`, the club route answers 401, and the transport
    // treats a 401 on a request it authenticated as the session ending. The
    // member app signed itself out on mount — `state-member-mode-exclusion`
    // predicted exactly this failure for the two hooks it covered, and this
    // one was never added to it.
    //
    // ⚠️ AND WHEN THIS GAINS A MEMBER PATH (#236), IT MUST RESET ON A CLUB
    // CHANGE. The player app now changes its active club IN PLACE rather than
    // by remount, and `refresh` MERGES each read into the state it holds — so a
    // real club-scoped read here would blend the previous club's conversations
    // into the next one's. Today it reads nothing on this surface, so there is
    // nothing to blend; see `useMemberBootstrap` for how the club data does it.
    if (playerSurfaceActive()) return emptyMessaging();
    const res = await Storage.get(MESSAGING_KEY);
    return res?.value ? sanitizeMessaging(JSON.parse(res.value)) : emptyMessaging();
  } catch {
    return emptyMessaging();
  }
}

export function useMessaging(opts: UseMessagingOptions = {}): MessagingApi {
  const { enabled = true, live = false, pollMs = MESSAGING_POLL_MS } = opts;
  const now = opts.now || Date.now;
  const nextId = opts.nextId || newId;
  const [state, setState] = useState<MessagingState>(emptyMessaging);
  const [loaded, setLoaded] = useState(false);
  const latest = useRef<MessagingState>(state);
  const inFlight = useRef(false);

  const apply = (next: MessagingState) => {
    latest.current = next;
    setState(next);
  };

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      // Not allowed to read the key: nothing to merge, and no request made.
      if (!enabled) return;
      const stored = await readStored();
      apply(mergeMessaging(latest.current, stored));
    } finally {
      inFlight.current = false;
      setLoaded(true);
    }
  }, [enabled]);

  // Mount read + optional polling while a messaging screen is on.
  useEffect(() => {
    let cancelled = false;
    void refresh().then(() => {
      if (cancelled) return;
    });
    if (!live || !enabled) return () => void (cancelled = true);
    const tick = () => void refresh();
    const id = setInterval(tick, pollMs);
    window.addEventListener('focus', tick);
    window.addEventListener('online', tick);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', tick);
      window.removeEventListener('online', tick);
    };
  }, [live, enabled, pollMs, refresh]);

  /**
   * Merge with the store, mutate, persist, then apply (Storage-first).
   *
   * SERIALISED PER KEY, WITHIN THIS RUNTIME. Read-merge-write is not atomic —
   * two awaits sit between reading the blob and writing it back — so two
   * commits started before the first has written would both build on the same
   * base, and the second write would drop the first one's message.
   * mergeMessaging cannot save that: it runs BEFORE the read lands, so the
   * second commit never sees the first. Going through the shared chain means
   * one commit is always complete before the next one reads.
   *
   * WHAT THIS DOES NOT SOLVE: anything outside this JavaScript runtime. Two
   * browser tabs, two phones, a phone and a banepad — each has its own chain
   * and its own copy of the blob, so both can read the same old value and
   * write over each other. mergeMessaging narrows that window (a write carries
   * forward whatever the writer had already read) but cannot close it: the
   * client cannot make read-merge-write atomic against a store that offers no
   * compare-and-set. Only the server can, which is #240 — a cutover constraint
   * on /member/messages, not something to fix here.
   */
  const commit = <T>(
    mutate: (s: MessagingState) => { state: MessagingState; result: T },
  ): Promise<T> =>
    serialise(MESSAGING_KEY, async () => {
      // REFUSES IN MEMBER MODE (#393), rather than writing nowhere.
      //
      // `commit` is the only write path, so this one check covers send,
      // openDm, broadcast, mute and the rest. A silent no-op would be the
      // worse failure: the optimistic state would show the message, the write
      // would go nowhere, and the next merge would erase it — a message the
      // sender believes was delivered.
      if (playerSurfaceActive() || !enabled) throw new MessagingUnavailable();
      // latest.current is read AFTER the await so it includes anything a
      // commit earlier in the chain applied — including one from ANOTHER hook
      // in this tab, whose write readStored() picks up.
      const stored = await readStored();
      const base = mergeMessaging(latest.current, stored);
      const { state: next, result } = mutate(base);
      await Storage.set(MESSAGING_KEY, JSON.stringify(next));
      apply(next);
      return result;
    });

  const send: MessagingApi['send'] = (conversationId, senderId, text) =>
    commit((s) => {
      // A member who switched messaging off sends nothing; a dm whose counterpart
      // did is closed for writing.
      if (isMessagingDisabled(s, senderId)) throw new Error('Meldinger er slått av');
      const conv = s.conversations.find((c) => c.id === String(conversationId));
      if (conv && conversationBlockedBy(s, conv, senderId))
        throw new Error('Mottakeren har slått av meldinger');
      const r = appendMessage(s, conversationId, senderId, text, now(), nextId);
      return { state: r.state, result: undefined };
    });

  const openDm: MessagingApi['openDm'] = (a, b) =>
    commit((s) => {
      const r = ensureDm(s, a, b, now(), nextId);
      return { state: r.state, result: r.conversation };
    });

  const openGroup: MessagingApi['openGroup'] = (creatorId, memberIds, title) =>
    commit((s) => {
      const r = createGroup(s, creatorId, memberIds, title, now(), nextId);
      return { state: r.state, result: r.conversation };
    });

  const openClubConversation: MessagingApi['openClubConversation'] = (memberId) =>
    commit((s) => {
      const r = ensureClubConversation(s, memberId, now(), nextId, String(memberId));
      return { state: r.state, result: r.conversation };
    });

  const read: MessagingApi['read'] = (conversationId, readerId) =>
    commit((s) => ({ state: markRead(s, conversationId, readerId, now()), result: undefined }));

  const broadcast: MessagingApi['broadcast'] = (memberIds, text) =>
    commit((s) => {
      const r = broadcastFromClub(s, memberIds, text, now(), nextId);
      return { state: r.state, result: { count: r.count } };
    });

  const openDynamic: MessagingApi['openDynamic'] = (rule) =>
    commit((s) => {
      const r = ensureDynamicConversation(s, rule, now());
      return { state: r.state, result: r.conversation };
    });

  const mute: MessagingApi['mute'] = (memberId, conversationId, muted) =>
    commit((s) => ({
      state: setMuted(s, memberId, conversationId, muted, now()),
      result: undefined,
    }));

  const setDisabled: MessagingApi['setDisabled'] = (memberId, disabled) =>
    commit((s) => ({
      state: setMessagingDisabled(s, memberId, disabled, now()),
      result: undefined,
    }));

  const leaveDynamic: MessagingApi['leaveDynamic'] = (memberId, clubId, left) =>
    commit((s) => ({ state: setLeftDynamic(s, memberId, clubId, left, now()), result: undefined }));

  return {
    state,
    loaded,
    unavailable: playerSurfaceActive() || !enabled,
    refresh,
    send,
    openDm,
    openGroup,
    openClubConversation,
    read,
    broadcast,
    openDynamic,
    mute,
    leaveDynamic,
    setDisabled,
  };
}
